/**
 * useOpenTaskCompose — the single way any surface opens the shared composer
 * as a TASK.
 *
 * It owns the three things every task entry point used to re-derive for
 * itself: the `ahoy_task` create permission, the default stage ("To Do", else
 * the first stage of the default task pipeline — see useDefaultMemoStage), and
 * the `openMemoCompose` call shape. Callers add only what is theirs: a linked
 * host record (record tabs, the Tasks section) or a focused date (the
 * calendar's header button and click-to-create slots).
 *
 * Behaviour is therefore identical everywhere: a stage-less org can still
 * create tasks (the requested `entityType` — not the presence of a stage — is
 * what makes the composition an `ahoy_task`), and the affordance is shown iff
 * `canCreateTask`.
 */

import { useCallback, useMemo } from 'react';

import type { MemoComposeLinkedEntity } from '@/contexts/MemoComposeContext';
import { useMemoCompose } from '@/contexts/MemoComposeContext';
import { usePermissions } from '@/hooks/usePermissions';

import { useDefaultMemoStage } from '../notes/useDefaultMemoStage';
import { TASK_ENTITY_TYPE } from './task.constants';

export interface OpenTaskComposeOptions {
  /**
   * Pre-fill the due. Either a plain ISO date (`"2026-01-15"`) or a full ISO
   * datetime (`"2026-01-15T14:30:00.000Z"`, e.g. a timed calendar slot).
   */
  date?: string;
  /** Records to pre-link the task to (the host record of a tab or section). */
  linkedEntities?: MemoComposeLinkedEntity[];
}

export interface OpenTaskCompose {
  /** Open the composer for a new task, with the default stage pre-selected. */
  openTaskCompose: (options?: OpenTaskComposeOptions) => void;
  /** Whether the current user may create tasks at all. */
  canCreateTask: boolean;
  /**
   * Whether the default task stage is still resolving (pipelines loading). While
   * true, callers should keep the task affordance pending rather than open an
   * unstaged task — the stage may resolve to "To Do" a moment later. Once
   * resolution completes it is false even on a stage-less org (which stays
   * creatable, just unstaged).
   */
  isStageLoading: boolean;
}

export function useOpenTaskCompose(): OpenTaskCompose {
  const { openMemoCompose } = useMemoCompose();
  const { hasPermission } = usePermissions();
  const canCreateTask = hasPermission(TASK_ENTITY_TYPE, 'ahoy_object_created');
  const defaultStageEntityId = useDefaultMemoStage(TASK_ENTITY_TYPE);
  // `undefined` = pipelines still loading (distinct from `null` = resolved, no
  // stage — see useDefaultMemoStage's three-valued contract).
  const isStageLoading = defaultStageEntityId === undefined;

  const openTaskCompose = useCallback(
    (options?: OpenTaskComposeOptions) => {
      // Belt-and-suspenders: entry points SHOULD disable their affordance while
      // the stage is still resolving, but guard here too so no caller — however
      // it's wired — can open an unstaged task before "To Do" arrives. Covers
      // handlers that can't easily be disabled (the calendar's slot-select) and
      // any future entry point that forgets to gate. `isStageLoading` is false
      // on a genuinely stage-less org, so this never blocks that legitimate
      // unstaged-task creation.
      if (isStageLoading) {
        return;
      }
      openMemoCompose({
        entityType: TASK_ENTITY_TYPE,
        // Stage-less tasks stay tasks; the requested entityType above (not the
        // stage) is what makes this an ahoy_task. A truthy check also skips the
        // still-loading `undefined` — callers gate the action on isStageLoading.
        ...(defaultStageEntityId
          ? { stageEntityId: defaultStageEntityId }
          : {}),
        ...options,
      });
    },
    [openMemoCompose, defaultStageEntityId, isStageLoading]
  );

  return useMemo(
    () => ({ openTaskCompose, canCreateTask, isStageLoading }),
    [openTaskCompose, canCreateTask, isStageLoading]
  );
}
